import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Eye, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { adminApi } from "@/lib/api";

export const TYPE_LABELS = {
  principal: "Brand Principal",
  buyer: "Trade Buyer",
  business: "Trade / Business",
  "brand-principal": "Brand Introduction",
  "hospitality-retail": "Hospitality & Retail",
  "logistics-supply": "Logistics & Supply",
  general: "General / Other",
};

const STATUS_STYLES = {
  new: "bg-blue-50 text-blue-700 border-blue-200",
  contacted: "bg-amber-50 text-amber-700 border-amber-200",
  closed: "bg-neutral-100 text-neutral-500 border-neutral-200",
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default function AdminEnquiries() {
  const [rows, setRows] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter !== "all") params.type = typeFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await adminApi.get("/admin/enquiries", { params });
      setRows(res.data);
    } catch {
      toast.error("Failed to load enquiries");
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await adminApi.patch(`/admin/enquiries/${id}/status`, { status });
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success("Status updated");
    } catch {
      toast.error("Failed to update status");
    }
  };

  const downloadFile = async (enq) => {
    try {
      const res = await adminApi.get(`/admin/enquiries/${enq.id}/file`, { responseType: "blob" });
      downloadBlob(res.data, enq.uploaded_file);
    } catch {
      toast.error("File download failed");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await adminApi.get("/admin/enquiries/export/csv", { responseType: "blob" });
      downloadBlob(res.data, "hcc-enquiries.csv");
      toast.success("CSV exported");
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div data-testid="admin-enquiries-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900">Enquiries</h1>
          <p className="text-sm text-neutral-500 mt-0.5">Trade, partner and contact submissions.</p>
        </div>
        <Button onClick={exportCsv} variant="outline" data-testid="enquiries-export-csv">
          <Download size={15} className="mr-2" /> Export CSV
        </Button>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-52 bg-white" data-testid="enquiries-filter-type">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {Object.entries(TYPE_LABELS).map(([v, l]) => (
              <SelectItem key={v} value={v}>
                {l}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44 bg-white" data-testid="enquiries-filter-status">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-5 bg-white border border-neutral-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Received</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Name / Company</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-neutral-400 py-10">
                  Loading…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-neutral-400 py-10" data-testid="enquiries-empty">
                  No enquiries found.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id} data-testid={`enquiry-row-${r.id}`}>
                  <TableCell className="whitespace-nowrap text-neutral-500">
                    {new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-normal">
                      {TYPE_LABELS[r.type] || r.type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium text-neutral-900">{r.name}</div>
                    {r.company && <div className="text-xs text-neutral-500">{r.company}</div>}
                  </TableCell>
                  <TableCell className="text-neutral-600">{r.email}</TableCell>
                  <TableCell>
                    <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                      <SelectTrigger
                        className={`w-32 h-8 text-xs border ${STATUS_STYLES[r.status] || ""}`}
                        data-testid={`enquiry-status-${r.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {r.uploaded_file && (
                      <Button variant="ghost" size="sm" onClick={() => downloadFile(r)} data-testid={`enquiry-file-${r.id}`} title="Download attachment">
                        <Paperclip size={15} />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => setSelected(r)} data-testid={`enquiry-view-${r.id}`}>
                      <Eye size={15} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="enquiry-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>{selected.name}</DialogTitle>
                <DialogDescription>Full enquiry details and attachment.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  <Info label="Type" value={TYPE_LABELS[selected.type] || selected.type} />
                  <Info label="Status" value={selected.status} />
                  <Info label="Company" value={selected.company} />
                  <Info label="Email" value={selected.email} />
                  <Info label="Phone" value={selected.phone} />
                  <Info label="Country" value={selected.country} />
                  <Info label="Category" value={selected.category} />
                  <Info label="Received" value={new Date(selected.created_at).toLocaleString("en-IN")} />
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-neutral-400">Message</p>
                  <p className="mt-1 whitespace-pre-wrap text-neutral-700 max-h-56 overflow-y-auto">{selected.message}</p>
                </div>
                {selected.uploaded_file && (
                  <Button variant="outline" size="sm" onClick={() => downloadFile(selected)}>
                    <Paperclip size={14} className="mr-2" /> Download attachment
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wider text-neutral-400">{label}</p>
    <p className="text-neutral-700">{value || "—"}</p>
  </div>
);
